import { useInfiniteQuery } from "@tanstack/react-query";
import { brandService } from "../api/services/brandService";
import type { Brand, BrandFilters, BrandResult } from "../schemas/brandSchema";
import { useBrands } from "./useBrands";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type BrandsInfiniteFilters = Omit<BrandFilters, "page" | "pageSize"> & {
  pageSize?: number;
};

export const useBrandsInfinite = (filters: BrandsInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useBrands(undefined, { enabled: false });

  const query = useInfiniteQuery<BrandResult, Error>({
    queryKey: ["brands", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      brandService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const brands: Brand[] = query.data?.pages.flatMap((p) => p.brands) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    brands,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
